<template>
  <div class="reum-editor">
    <Ckeditor
      v-model="comp.content"
      :editor="comp.editor"
      :config="comp.config"
      @ready="comp.onReady"
    ></Ckeditor>
  </div>
</template>

<script>
import { onMounted, reactive, watch } from "vue";
import { component as Ckeditor } from "@ckeditor/ckeditor5-vue";
import {
  ClassicEditor,
  Essentials,
  Autoformat,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Link,
  List,
  MediaEmbed,
  Paragraph,
  Table,
  TableToolbar,
  Alignment,
  BlockQuote,
  FontSize,
  FontColor,
  FontBackgroundColor,
  SpecialCharacters,
  SpecialCharactersEssentials,
} from "ckeditor5";
import { ImageResizeEditing, ImageResizeHandles } from "@ckeditor/ckeditor5-image/dist/index.js";
import "ckeditor5/ckeditor5.css";
import axios from "@/plugins/axios.js";

// 이모지 카테고리 플러그인
function SpecialCharactersEmoji(editor) {
  editor.plugins.get("SpecialCharacters").addItems(
    "Emoji",
    [
      { title: "😀 웃는 얼굴", character: "😀" },
      { title: "😊 미소", character: "😊" },
      { title: "😂 눈물 웃음", character: "😂" },
      { title: "😍 사랑", character: "😍" },
      { title: "🥰 설렘", character: "🥰" },
      { title: "😎 멋짐", character: "😎" },
      { title: "🤔 생각", character: "🤔" },
      { title: "😢 슬픔", character: "😢" },
      { title: "😡 화남", character: "😡" },
      { title: "👍 좋아요", character: "👍" },
      { title: "👎 싫어요", character: "👎" },
      { title: "👏 박수", character: "👏" },
      { title: "🙌 만세", character: "🙌" },
      { title: "🙏 부탁", character: "🙏" },
      { title: "✅ 체크", character: "✅" },
      { title: "❌ 엑스", character: "❌" },
      { title: "⭐ 별", character: "⭐" },
      { title: "🔥 불꽃", character: "🔥" },
      { title: "💡 아이디어", character: "💡" },
      { title: "📢 공지", character: "📢" },
      { title: "📌 핀", character: "📌" },
      { title: "📎 첨부", character: "📎" },
      { title: "🎉 축하", character: "🎉" },
      { title: "🎊 파티", character: "🎊" },
      { title: "💬 말풍선", character: "💬" },
      { title: "❤️ 하트", character: "❤️" },
      { title: "💙 파란 하트", character: "💙" },
      { title: "💚 초록 하트", character: "💚" },
      { title: "⚠️ 경고", character: "⚠️" },
      { title: "ℹ️ 정보", character: "ℹ️" },
    ],
    { label: "이모지" }
  );
}

class CustomUploadAdapter {
  constructor(loader, etype) {
    this.loader = loader;
    this.etype = etype;
  }

  upload() {
    return this.loader.file.then(
      (file) =>
        new Promise((resolve, reject) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("etype", this.etype);

          axios
            .post("/admin/upload", formData)
            .then((res) => {
              if (res.data.success) {
                resolve({ default: res.data.data.uploadUrl });
              } else {
                reject(res.data.err_msg);
              }
            })
            .catch(reject);
        })
    );
  }

  abort() {}
}

function CustomUploadAdapterPlugin(editor) {
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
    return new CustomUploadAdapter(loader, editor.config.get("etype"));
  };
}

export default {
  name: "BagicEditor",
  components: { Ckeditor },
  props: {
    modelValue: { type: String, default: "" },
    etype: { type: String, default: "temp" },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const comp = reactive({
      editor: ClassicEditor,
      content: "",

      config: {
        etype: props.etype,
        extraPlugins: [CustomUploadAdapterPlugin, SpecialCharactersEmoji],
        plugins: [
          Essentials,
          Autoformat,
          Bold,
          Italic,
          Underline,
          Strikethrough,
          Image,
          ImageCaption,
          ImageResizeEditing,
          ImageResizeHandles,
          ImageStyle,
          ImageToolbar,
          ImageUpload,
          Indent,
          IndentBlock,
          Link,
          List,
          MediaEmbed,
          Paragraph,
          Table,
          TableToolbar,
          Alignment,
          BlockQuote,
          FontSize,
          FontColor,
          FontBackgroundColor,
          SpecialCharacters,
          SpecialCharactersEssentials,
        ],
        toolbar: {
          items: [
            "bold",
            "italic",
            "underline",
            "strikethrough",
            "|",
            "fontSize",
            "fontColor",
            "fontBackgroundColor",
            "|",
            "alignment",
            "|",
            "bulletedList",
            "numberedList",
            "outdent",
            "indent",
            "|",
            "link",
            "uploadImage",
            "blockQuote",
            "insertTable",
            "mediaEmbed",
            "|",
            "specialCharacters",
            "|",
            "undo",
            "redo",
          ],
        },
        image: {
          toolbar: [
            "imageStyle:alignLeft",
            "imageStyle:alignCenter",
            "imageStyle:alignRight",
            "|",
            "toggleImageCaption",
            "imageTextAlternative",
          ],
          resizeUnit: "%",
        },
        table: {
          contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
        },
        fontSize: {
          options: [10, 12, 14, "default", 18, 20, 24, 28, 36],
        },
      },

      onReady(editor) {
        console.log("CKEditor5 is ready", editor);
      },
    });

    watch(
      () => comp.content,
      (nVal) => emit("update:modelValue", nVal)
    );

    watch(
      () => props.modelValue,
      (nVal, oVal) => {
        if (nVal !== oVal) comp.content = nVal;
      }
    );

    watch(
      () => props.etype,
      (nVal) => { comp.config.etype = nVal; }
    );

    onMounted(() => {
      comp.content = props.modelValue;
    });

    return { comp };
  },
};
</script>
<style>
.reum-editor {
  width: 100%;
  height: 100%;
}

.reum-editor .ck.ck-editor__editable[role='textbox'] {
  min-height: 500px;
}
</style>
